import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Skeleton from "./Skeleton";
import "../../style/Card.css";

// flexible card component that can be used for counter cards (large value display), info cards (icon + label + value), system info cards (title + content)
const Card = ({
  // content props
  icon,
  iconColor = "var(--role-accent)",
  title,
  description,
  value,
  children,

  // styling props
  variant = "default",
  colorClass = "",
  className = "",
  darkMode = false,

  // layout props
  valuePosition = "top",
  
  // loading state
  isLoading = false,
  
  // animation state - triggers slide-up when data updates
  justUpdated = false,
}) => {
  // build the className string
  const cardClasses = [
    "card",
    `card--${variant}`,
    colorClass,
    darkMode ? "dark-mode" : "light-mode",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // value animation class
  const valueClasses = [
    "card__value",
    justUpdated ? "card__value--slide-up" : "",
  ].filter(Boolean).join(" ");

  return (
    <div className={cardClasses}>
      <div className="card__content">
        {/* counter variant - Value on top with icon */}
        {variant === "counter" && (
          <>
            <div className="card__value-container">
              {icon && (
                <div className="card__icon">
                  <FontAwesomeIcon icon={icon} />
                </div>
              )}
              <div className={valueClasses}>
                {isLoading ? (
                  <Skeleton width="60px" height="36px" borderRadius="6px" />
                ) : (
                  value
                )}
              </div>
            </div>
            {/* Title and description for counter (no icon here) */}
            {title && <span className="card__title">{title}</span>}
            {description && (
              <div className="card__description">{description}</div>
            )}
          </>
        )}

        {/* Info variant - icon + title + value */}
        {variant === "info" && (
          <>
            {/* header with icon and title */}
            {(icon || title) && (
              <div className="card__header">
                {icon && (
                  <FontAwesomeIcon
                    icon={icon}
                    className="card__icon"
                    style={{ color: iconColor }}
                  />
                )}
                {title && <span className="card__title">{title}</span>}
              </div>
            )}

            {/* value below title */}
            {value !== undefined && (
              <div className={valueClasses}>
                {isLoading ? (
                  <Skeleton width="80px" height="28px" borderRadius="4px" />
                ) : (
                  value
                )}
              </div>
            )}

            {/* description text */}
            {description && (
              <div className="card__description">{description}</div>
            )}
          </>
        )}

        {/* Default and system variants */}
        {variant !== "counter" && variant !== "info" && (
          <>
            {value && (
              <div className={valueClasses}>
                {isLoading ? (
                  <Skeleton width="80px" height="28px" borderRadius="4px" />
                ) : (
                  value
                )}
              </div>
            )}

            {/* header with icon and title */}
            {(icon || title) && (
              <div className="card__header">
                {icon && (
                  <FontAwesomeIcon
                    icon={icon}
                    className="card__icon"
                    style={{ color: iconColor }}
                  />
                )}
                {title && <span className="card__title">{title}</span>}
              </div>
            )}

            {/* description text */}
            {description && (
              <div className="card__description">{description}</div>
            )}
          </>
        )}

        {/* custom content for system-info or other uses */}
        {children}
      </div>
    </div>
  );
};

Card.propTypes = {
  icon: PropTypes.object,
  iconColor: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node,
  variant: PropTypes.oneOf(["default", "counter", "info", "system"]),
  colorClass: PropTypes.string,
  className: PropTypes.string,
  darkMode: PropTypes.bool,
  valuePosition: PropTypes.oneOf(["top", "bottom"]),
  isLoading: PropTypes.bool,
  justUpdated: PropTypes.bool,
};

export default Card;
